/* eslint-disable @typescript-eslint/no-explicit-any */
import { SupabaseClient } from '@supabase/supabase-js';

import { Analysis, AnalysisMetadata, AnalysisCloudStatus, AnalysisData, Database, withLogs } from '@core';

type SwingAnalysisCombined = {
    activityID: number;
    analysis: AnalysisData;
    isQuickAnalysis: boolean;
    metadata: AnalysisMetadata;
};

export type MappedAnalysisMetadata = { usedMarkers?: boolean; golfBallTranslation: [number, number, number] };

export type SwingAnalysis = Record<'quickAnalysis' | 'fullAnalysis', Analysis | null> & {
    metadata: MappedAnalysisMetadata;
};

const mapAnalysisCombinedToAnalysis = (uuid: string, analysis: AnalysisData): Analysis => {
    return {
        uuid,
        data: { analysis },
    };
};

const mapAnalysisMetadata = (metadata: AnalysisMetadata) => ({
    usedMarkers: metadata?.used_markers,
    golfBallTranslation: metadata?.golf_ball_translation ?? [0, 0, 0],
});

const mapSwingAnalysis = (uuid: string, data: SwingAnalysisCombined): SwingAnalysis => {
    const analysis = mapAnalysisCombinedToAnalysis(uuid, data?.analysis);
    const metadata = mapAnalysisMetadata(data.metadata);
    const isQuickAnalysis = data?.isQuickAnalysis;

    //TODO(at): Refactor usages to use one analysis object with isQuickAnalysis flag
    return {
        quickAnalysis: isQuickAnalysis ? analysis : null,
        fullAnalysis: isQuickAnalysis ? null : analysis,
        metadata,
    };
};

/**
 * Fetches analysis for a swing.
 *
 * @param supabase - Supabase client
 * @param swingUUID - UUID of the swing
 * @returns Analysis and metadata for the swing
 */
export const fetchSwingAnalysis = withLogs(
    async (supabase: SupabaseClient<Database>, swingUUID: string): Promise<SwingAnalysis> => {
        const { data, error } = await supabase.rpc('get_swing_analysis', {
            swing_uuid: swingUUID,
        });

        if (error) {
            throw error;
        }

        const analysisData: SwingAnalysisCombined[] | null = data as any;
        if (!analysisData || analysisData.length === 0) {
            throw new Error('No analysis found for swing');
        }

        return mapSwingAnalysis(swingUUID, analysisData[0]);
    },
    'fetchSwingAnalysis',
);

export type FullAnalysisPayload = {
    commit_timestamp: string;
    eventType: 'UPDATE';
    new: {
        swing_id: number;
        type_id: 1 | 2; // 1 = quick analysis, 2 = full analysis
        created_at: string;
        status: AnalysisCloudStatus;
    };
};

export const subscribeToSwingAnalysis = (
    supabase: SupabaseClient<Database>,
    handler: (payload: FullAnalysisPayload) => Promise<void>,
) => {
    return supabase
        .channel('channel')
        .on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'full_analysis_job_queue',
            },
            async (payload: any) => {
                try {
                    await handler(payload);
                } catch (error) {
                    console.error('Error processing change:', error);
                }
            },
        )
        .subscribe((status, error) => {
            if (status === 'CHANNEL_ERROR') {
                console.error(error);
                console.error('Full analysis subscription error:', status);
            }
        });
};
